/**
 * Health-check helper for the configuration page (Story 302).
 *
 * Wraps `ClaudeProvider.healthCheck()` with two affordances the form needs:
 *
 * 1. The result type matches `<Banner>` shapes — `ok: boolean`, `latencyMs`,
 *    and a verbatim `message` string we render to the user.
 * 2. A surface that does NOT throw on auth/network errors — the form must
 *    render the failure inline rather than crash. Errors from the provider
 *    are caught and reshaped into the negative result.
 *
 * The function is parameterised on a provider factory so unit tests can
 * inject a mock without spinning up the Anthropic SDK.
 */
import type { ClaudeConfig } from '@lib/claude-config';

export interface HealthCheckResult {
  ok: boolean;
  latencyMs: number;
  message: string;
}

interface ProviderShape {
  healthCheck(): Promise<{ ok: boolean; latencyMs: number; message?: string }>;
  connect(config: ClaudeConfig): Promise<unknown>;
  disconnect(): Promise<void>;
}

export type ProviderFactory = () => ProviderShape;

export async function runHealthCheck(
  config: ClaudeConfig,
  factory: ProviderFactory,
): Promise<HealthCheckResult> {
  const provider = factory();
  try {
    await provider.connect(config);
    const result = await provider.healthCheck();
    return {
      ok: result.ok,
      latencyMs: Math.max(0, Math.round(result.latencyMs)),
      message: result.message ?? (result.ok ? 'Connection healthy.' : 'Health check failed.'),
    };
  } catch (err) {
    const message = err instanceof Error && err.message ? err.message : 'Unexpected error during health check.';
    return { ok: false, latencyMs: 0, message };
  } finally {
    try {
      await provider.disconnect();
    } catch {
      // disconnect failures are non-fatal for the form UX.
    }
  }
}
